import { useState } from "react";
import { Copy, ExternalLink, Pencil, Search, ShieldCheck, Terminal } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const deriveWebfigPort = (port) => {
  const s = String(port || "");
  return /^5[02]\d{3}$/.test(s) ? Number("55" + s.slice(2)) : 0;
};

const salin = (text, label) => {
  try {
    navigator.clipboard?.writeText(text)?.then(() => toast.success(`${label} disalin`)).catch(() => toast.info(`${label}: ${text}`));
  } catch { toast.info(`${label}: ${text}`); }
};

export default function Winbox() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const { routers, refresh } = useRouters();
  const [query, setQuery] = useState("");
  const [editRouter, setEditRouter] = useState(null);
  const [credForm, setCredForm] = useState({ username: "", password: "", pin: "" });
  const [busy, setBusy] = useState(false);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="winbox-forbidden">
        <ShieldCheck className="w-10 h-10 text-rose-500" />
        <p className="text-muted-foreground text-sm">Hanya Admin Fasnet / Super Admin yang dapat membuka menu Winbox.</p>
      </div>
    );
  }

  const openEdit = (r) => {
    setCredForm({ username: r.username || "", password: r.password || "", pin: "" });
    setEditRouter(r);
  };

  const submitEditCred = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/verify-pin", { pin: credForm.pin });
      await api.put(`/routers/${editRouter.id}/credentials`, { username: credForm.username, password: credForm.password });
      toast.success(`Kredensial ${editRouter.name} diperbarui`);
      setEditRouter(null);
      refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const q = query.trim().toLowerCase();
  const cabang = routers.filter((r) => !r.is_induk && (!q || r.name.toLowerCase().includes(q)));

  return (
    <div className="space-y-5" data-testid="winbox-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Terminal className="w-6 h-6 text-[#F04E23]" /> Winbox
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Akses MikroTik langsung dari sini — klik tombol WebFig di sebelah nama cabang untuk membuka WebFig (tampilan Winbox versi web).</p>
      </div>

      <Card className="bg-card border-border shadow-sm" data-testid="winbox-cabang-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap space-y-0">
          <CardTitle className="text-base font-heading">Kredensial Winbox Semua Cabang ({cabang.length})</CardTitle>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari cabang..." className="pl-9 w-52 h-9" data-testid="input-winbox-search" />
          </div>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-4 pb-4" data-testid="winbox-grid">
          {!cabang.length && (
            <div className="col-span-full text-center py-8 text-muted-foreground text-sm" data-testid="winbox-empty">
              {q ? "Cabang tidak ditemukan" : "Belum ada router cabang"}
            </div>
          )}
          {cabang.map((r, idx) => {
            const wfPort = r.webfig_port || deriveWebfigPort(r.port) || deriveWebfigPort(r.winbox_port);
            return (
              <div key={r.id} className="rounded-xl border border-border bg-accent/20 px-3 py-2" data-testid={`winbox-row-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-muted-foreground shrink-0 w-6 text-right">{idx + 1}</span>
                  <span className="font-semibold text-xs truncate">{r.name}</span>
                  <div className="flex items-center gap-1 ml-auto shrink-0">
                    <Button size="sm" variant="outline" className="h-6 w-6 p-0" title="Edit login & password" onClick={() => openEdit(r)} data-testid={`wb-edit-${r.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {wfPort > 0 && (
                      <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] text-[#F04E23] border-[#F04E23]/40 hover:bg-[#F04E23]/10" onClick={() => window.open(`http://${r.host}:${wfPort}`, "_blank")} data-testid={`wb-webfig-${r.id}`}>
                        <ExternalLink className="w-3 h-3 mr-0.5" /> WebFig
                      </Button>
                    )}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground truncate mt-0.5 pl-8" data-testid={`wb-webfig-addr-${r.id}`}>{wfPort > 0 ? `${r.host}:${wfPort}` : "-"}</div>
                <div className="mt-0.5 pl-8 flex items-center gap-1 flex-wrap font-mono text-[10px]">
                  <span className="text-muted-foreground">Login:</span>
                  <span className="font-semibold" data-testid={`wb-login-text-${r.id}`}>{r.username || "-"}</span>
                  <button type="button" title="Salin login" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => salin(r.username, "Login")} data-testid={`wb-copy-user-${r.id}`}>
                    <Copy className="w-3 h-3" />
                  </button>
                  <span className="text-muted-foreground ml-2">Sandi:</span>
                  <span className="font-semibold" data-testid={`wb-pass-text-${r.id}`}>{r.password || "-"}</span>
                  <button type="button" title="Salin password" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => salin(r.password, "Password")} data-testid={`wb-copy-pass-${r.id}`}>
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Dialog open={!!editRouter} onOpenChange={(o) => !o && setEditRouter(null)}>
        <DialogContent data-testid="dialog-edit-cred">
          <DialogHeader><DialogTitle className="font-heading">Edit Kredensial — {editRouter?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitEditCred} className="space-y-3">
            <div className="space-y-1.5"><Label>Login / Username</Label><Input value={credForm.username} onChange={(e) => setCredForm({ ...credForm, username: e.target.value })} required data-testid="input-cred-username" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })} required data-testid="input-cred-password" /></div>
            <div className="space-y-1.5"><Label>PIN Super Admin (wajib)</Label><Input type="password" inputMode="numeric" value={credForm.pin} onChange={(e) => setCredForm({ ...credForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-cred-admin-pin" /></div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="btn-save-cred">{busy ? "Memproses..." : "Simpan Kredensial"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
